import axios from 'axios'

import { getToken, ensureFreshToken } from './tokenStore'

export const API_BASE_URL = import.meta.env.VITE_API_URL || ''

/** Build common request headers (auth + origin verification for local dev). */
export function getApiHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  const token = getToken()
  if (token) {
    headers['X-App-Authorization'] = `Bearer ${token}`
  }
  // Vite tree-shakes this entire block out of production builds
  if (import.meta.env.DEV) {
    const originSecret = import.meta.env.VITE_CF_ORIGIN_SECRET
    if (originSecret) {
      headers['X-Origin-Verify'] = originSecret
    }
  }
  return headers
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Ensure token is fresh, then attach headers on every request
apiClient.interceptors.request.use(
  async (config) => {
    await ensureFreshToken()
    Object.assign(config.headers, getApiHeaders())
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor - handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response

      switch (status) {
        case 401:
          // Unauthorized - redirect to login or refresh token
          console.error('Unauthorized - please log in')
          break
        case 403:
          // Forbidden
          console.error('Access forbidden')
          break
        case 404:
          console.error('Resource not found')
          break
        case 500:
          console.error('Server error')
          break
        default:
          console.error('API error:', data?.message || error.message)
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('No response from server')
    } else {
      // Something else happened
      console.error('Error:', error.message)
    }

    return Promise.reject(error)
  }
)
